import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faEdit, faTrash, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { getNoteColorStyle } from '../TicketNotesModal';
import TicketNotesModal from '../TicketNotesModal';
import { useTicketNotes } from '../../hooks/useTicketNotes';

const SidebarTicketNotes = ({
  ticket,
  notes: propNotes,
  isLoadingNotes: propIsLoadingNotes,
  isSavingNote: propIsSavingNote,
  createNote: propCreateNote,
  updateNote: propUpdateNote,
  deleteNote: propDeleteNote
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [openPaletteId, setOpenPaletteId] = useState(null);
  const [localColors, setLocalColors] = useState({});

  const activeTicketId = ticket?.idTicket || ticket?.id;

  // Si no se pasaron props de notas, se gestionan de forma autónoma con useTicketNotes
  const hookData = useTicketNotes({
    activeTicketId: propNotes !== undefined ? null : activeTicketId,
    token
  });

  const notes = propNotes !== undefined ? propNotes : hookData.notes;
  const isLoadingNotes = propIsLoadingNotes !== undefined ? propIsLoadingNotes : hookData.isLoadingNotes;
  const isSavingNote = propIsSavingNote !== undefined ? propIsSavingNote : hookData.isSavingNote;
  const createNote = propCreateNote || hookData.createNote;
  const updateNote = propUpdateNote || hookData.updateNote;
  const deleteNote = propDeleteNote || hookData.deleteNote;

  // Refs para listeners de eventos de clic fuera
  const notesRef = React.useRef(notes);
  notesRef.current = notes;
  const localColorsRef = React.useRef(localColors);
  localColorsRef.current = localColors;
  const updateNoteRef = React.useRef(updateNote);
  updateNoteRef.current = updateNote;

  // Cerrar popover al hacer clic fuera o presionar Escape (y guardar color pendiente si hubo cambio)
  useEffect(() => {
    if (!confirmDeleteId && !openPaletteId) return;

    const savePendingColorOnClose = async (paletteNoteId) => {
      if (!paletteNoteId) return;
      const currentNote = notesRef.current.find(n => n.idTicketNotas === paletteNoteId);
      const pendingColor = localColorsRef.current[paletteNoteId];
      if (
        currentNote &&
        pendingColor &&
        pendingColor.toLowerCase() !== (currentNote.noteColor || '').toLowerCase()
      ) {
        try {
          await updateNoteRef.current({
            idTicketNotas: currentNote.idTicketNotas,
            note: currentNote.note,
            noteColor: pendingColor
          });
          showSuccessToast('Color de nota actualizado');
        } catch (err) {
          showErrorToast(err.message || 'Error al cambiar el color de la nota');
        }
      }
    };

    const handleClickOutside = (e) => {
      if (!e.target.closest('.note-action-popover')) {
        if (openPaletteId) {
          savePendingColorOnClose(openPaletteId);
        }
        setConfirmDeleteId(null);
        setOpenPaletteId(null);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (openPaletteId) {
          savePendingColorOnClose(openPaletteId);
        }
        setConfirmDeleteId(null);
        setOpenPaletteId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [confirmDeleteId, openPaletteId, showSuccessToast, showErrorToast]);

  if (!activeTicketId) return null;

  const handleOpenCreate = () => {
    setEditingNote(null);
    setOpenPaletteId(null);
    setConfirmDeleteId(null);
    setShowModal(true);
  };

  const handleStartEdit = (noteItem, e) => {
    e?.stopPropagation();
    setOpenPaletteId(null);
    setConfirmDeleteId(null);
    const effectiveColor = localColors[noteItem.idTicketNotas] || noteItem.noteColor;
    setEditingNote({ ...noteItem, noteColor: effectiveColor });
    setShowModal(true);
  };

  const handleDelete = async (noteItem, e) => {
    e?.stopPropagation();
    setIsDeletingId(noteItem.idTicketNotas);
    try {
      await deleteNote({ idTicketNotas: noteItem.idTicketNotas });
      showSuccessToast('Nota eliminada correctamente');
      setConfirmDeleteId(null);
    } catch (err) {
      showErrorToast(err.message || 'Error al eliminar la nota');
    } finally {
      setIsDeletingId(null);
    }
  };

  return (
    <div>
      {/* Header de la sección */}
      <div
        className="fw-semibold mb-2 pb-1 d-flex align-items-center justify-content-between"
        style={{
          fontSize: '0.68rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: isDark ? '#94a3b8' : '#64748b'
        }}
      >
        <div className="d-flex align-items-center gap-1.5">
          <span>NOTA DE TICKET</span>
          {notes.length > 0 && (
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                color: isDark ? '#fbbf24' : '#b45309',
                fontSize: '0.65rem',
                padding: '0.2em 0.55em',
                fontWeight: '600'
              }}
            >
              {notes.length}
            </span>
          )}
          {isLoadingNotes && (
            <FontAwesomeIcon icon={faSpinner} spin className="text-primary ms-1" style={{ fontSize: '0.7rem' }} />
          )}
        </div>

        <button
          type="button"
          onClick={handleOpenCreate}
          className="btn btn-link p-0 text-primary fw-semibold shadow-none border-0 d-flex align-items-center gap-1"
          style={{
            fontSize: '0.72rem',
            textDecoration: 'none',
            cursor: 'pointer'
          }}
          title="Agregar nueva nota"
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />
          <span>Agregar</span>
        </button>
      </div>

      {/* Lista / Visualización de las notas */}
      {isLoadingNotes && notes.length === 0 ? (
        <div className="text-center py-3 text-muted" style={{ fontSize: '0.75rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-primary" />
          Cargando notas...
        </div>
      ) : notes.length === 0 ? (
        <div
          onClick={handleOpenCreate}
          className="py-3 px-3 rounded-3 border text-center transition-all"
          style={{
            fontSize: '0.75rem',
            backgroundColor: isDark ? '#0b1727' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#64748b',
            cursor: 'pointer'
          }}
          title="Clic para agregar una nota"
        >
          <div className="d-flex align-items-center justify-content-center gap-2 py-1">
            <FontAwesomeIcon icon={faStickyNote} />
            <span>Este ticket no tiene notas.</span>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {notes.map((item) => {
            const effectiveColor = localColors[item.idTicketNotas] || item.noteColor;
            const noteColorStyle = getNoteColorStyle(effectiveColor, isDark);
            const isConfirmingDelete = confirmDeleteId === item.idTicketNotas;
            const isDeleting = isDeletingId === item.idTicketNotas;

            return (
              <div
                key={item.idTicketNotas}
                className="rounded-3 border position-relative transition-all d-flex justify-content-between align-items-start gap-2"
                style={{
                  ...noteColorStyle,
                  padding: '12px 14px',
                  borderWidth: '1px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}
              >
                {/* Contenido / Cuerpo de la nota con buen espaciado */}
                <div
                  className="flex-grow-1"
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: '1.45',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: noteColorStyle.color || (isDark ? '#f9fafd' : '#1e293b')
                  }}
                >
                  {item.note}
                </div>

                {/* Botones de acción limpios (sin fondos ni sombras) */}
                <div className="d-flex align-items-center flex-shrink-0 pt-0.5 position-relative" style={{ gap: '10px' }}>
               

                  {/* Botón de Editar */}
                  <button
                    type="button"
                    onClick={(e) => handleStartEdit(item, e)}
                    className="btn btn-link p-0 text-decoration-none shadow-none border-0 text-secondary"
                    style={{
                      opacity: 0.65,
                      cursor: 'pointer',
                      transition: 'opacity 0.15s ease'
                    }}
                    title="Editar nota"
                  >
                    <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.82rem' }} />
                  </button>

                  {/* Botón de Eliminar con modalito / popover flotante arriba */}
                  <div className="position-relative note-action-popover">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenPaletteId(null);
                        setConfirmDeleteId(isConfirmingDelete ? null : item.idTicketNotas);
                      }}
                      className="btn btn-link p-0 text-decoration-none shadow-none border-0 text-danger"
                      style={{
                        opacity: isConfirmingDelete ? 1 : 0.75,
                        cursor: 'pointer',
                        transition: 'opacity 0.15s ease'
                      }}
                      title="Eliminar nota"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.82rem' }} />
                    </button>

                    {/* Modalito flotante de confirmación arriba del botecito */}
                    {isConfirmingDelete && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="position-absolute shadow-lg rounded-3 d-flex flex-column align-items-center"
                        style={{
                          bottom: 'calc(100% + 10px)',
                          right: '-6px',
                          zIndex: 1060,
                          minWidth: '140px',
                          padding: '10px 12px',
                          backgroundColor: isDark ? '#1e293b' : '#ffffff',
                          border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                          boxShadow: '0 10px 25px rgba(0,0,0,0.22)'
                        }}
                      >
                        <span style={{ fontSize: '0.74rem', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: '8px' }}>
                          ¿Eliminar nota?
                        </span>
                        <div className="d-flex align-items-center justify-content-center w-100" style={{ gap: '10px' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger py-1 px-2.5 fw-semibold shadow-none border-0"
                            style={{ fontSize: '0.72rem', borderRadius: '6px' }}
                            onClick={(e) => handleDelete(item, e)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? '...' : 'Sí'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm py-1 px-2.5 fw-semibold shadow-none"
                            style={{
                              fontSize: '0.72rem',
                              borderRadius: '6px',
                              backgroundColor: isDark ? '#1e293b' : '#ffffff',
                              color: isDark ? '#38bdf8' : '#0284c7',
                              border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(null);
                            }}
                            disabled={isDeleting}
                          >
                            No
                          </button>
                        </div>
                        {/* Flechita hacia abajo apuntando al botecito */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: '10px',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: isDark ? '6px solid #1e293b' : '6px solid #ffffff'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para Creación / Edición de Nota */}
      {showModal && (
        <TicketNotesModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingNote(null);
          }}
          ticket={ticket}
          noteToEdit={editingNote}
          isSavingNote={isSavingNote}
          createNote={createNote}
          updateNote={async (payload) => {
            if (payload?.idTicketNotas && payload?.noteColor) {
              setLocalColors(prev => ({ ...prev, [payload.idTicketNotas]: payload.noteColor }));
            }
            return await updateNote(payload);
          }}
        />
      )}
    </div>
  );
};

SidebarTicketNotes.propTypes = {
  ticket: PropTypes.object.isRequired,
  notes: PropTypes.array,
  isLoadingNotes: PropTypes.bool,
  isSavingNote: PropTypes.bool,
  createNote: PropTypes.func,
  updateNote: PropTypes.func,
  deleteNote: PropTypes.func
};

export default SidebarTicketNotes;
